import axios from "axios";
import { getDeviceId } from "./device";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// --- Native offline detection -------------------------------------------
// When the app runs inside the Capacitor native shell we serve the book,
// audio, trail config and search index from assets bundled inside the app,
// so it reads fully offline. On the web it keeps talking to the backend.
export const IS_NATIVE =
  typeof window !== "undefined" &&
  !!window.Capacitor &&
  (typeof window.Capacitor.isNativePlatform === "function"
    ? window.Capacitor.isNativePlatform()
    : window.Capacitor.getPlatform && window.Capacitor.getPlatform() !== "web");

const PUBLIC = process.env.PUBLIC_URL || "";
export const OFFLINE_DIR = `${PUBLIC}/offline`;

export const PDF_URL = IS_NATIVE ? `${OFFLINE_DIR}/braced_book.pdf` : `${API}/book/pdf?v=12`;

// Build the PDF URL, passing the reader's access code + device id so book
// owners on a registered device get the full file, while free/unauthenticated
// readers (or unregistered devices) receive only the free pages.
export function bookPdfUrl(code) {
  if (IS_NATIVE) return `${OFFLINE_DIR}/braced_book.pdf`;
  const base = `${API}/book/pdf?v=12`;
  if (!code) return base;
  return `${base}&code=${encodeURIComponent(code)}&device_id=${encodeURIComponent(getDeviceId())}`;
}

// Redeem an access code for the current device (registers it against the
// code's device cap). Resolves to the entitlement; rejects with an Error whose
// message is the gentle server detail (e.g. device-limit reached).
export async function restoreEntitlement(code) {
  try {
    const { data } = await axios.post(`${API}/entitlement/restore`, {
      code: (code || "").trim(),
      device_id: getDeviceId(),
    });
    return data;
  } catch (e) {
    const detail = e?.response?.data?.detail;
    throw new Error(detail || "That access code wasn't found.");
  }
}
// Best-effort: record that a shared access-code link was opened (for admin
// partnership tracking). Never blocks the reader flow.
export function recordCodeOpen(code) {
  try {
    axios.post(`${API}/codes/${encodeURIComponent((code || "").trim())}/opened`, {
      device_id: getDeviceId(),
    }).catch(() => {});
  } catch (e) {}
}
export const AMBIENT_AUDIO_URL = IS_NATIVE ? `${OFFLINE_DIR}/ambient.mp3` : `${API}/audio/ambient?v=2`;
export const PAGE_TURN_AUDIO_URL = IS_NATIVE ? `${OFFLINE_DIR}/page_turn.mp3` : `${API}/audio/page-turn?v=2`;
export const chapterAudioUrl = (id) => `${API}/audio/chapter/${id}?v=1`;
export const trackAudioUrl = (id) =>
  IS_NATIVE ? `${OFFLINE_DIR}/tracks/${id}.mp3` : `${API}/audio/track/${id}?v=1`;

export async function fetchBook() {
  if (IS_NATIVE) {
    const { data } = await axios.get(`${OFFLINE_DIR}/book.json`);
    return data;
  }
  const { data } = await axios.get(`${API}/book`);
  return data;
}

// Offline full-text search over the bundled page index (mirrors backend logic).
let _offlineIndex = null;
async function offlineSearch(q) {
  const query = (q || "").trim();
  if (query.length < 2) return { query, results: [] };
  if (!_offlineIndex) {
    const { data } = await axios.get(`${OFFLINE_DIR}/search-index.json`);
    _offlineIndex = data.pages || [];
  }
  const ql = query.toLowerCase();
  const results = [];
  for (let i = 0; i < _offlineIndex.length; i += 1) {
    const text = _offlineIndex[i] || "";
    const low = text.toLowerCase();
    const pos = low.indexOf(ql);
    if (pos === -1) continue;
    const start = Math.max(0, pos - 60);
    const end = Math.min(text.length, pos + query.length + 100);
    let snippet = text.slice(start, end).replace(/\s+/g, " ").trim();
    if (start > 0) snippet = "… " + snippet;
    if (end < text.length) snippet = snippet + " …";
    results.push({ page: i + 1, snippet, count: low.split(ql).length - 1 });
    if (results.length >= 80) break;
  }
  return { query, results };
}

export async function searchBook(q) {
  if (IS_NATIVE) return offlineSearch(q);
  let code = null;
  try { code = JSON.parse(localStorage.getItem("bnm-entitlement") || "null")?.access_code || null; } catch (e) {}
  const { data } = await axios.get(`${API}/search`, { params: code ? { q, code } : { q } });
  return data;
}
